import React, { useMemo, useState } from "react";
import { Popover, PopoverContent, PopoverTrigger } from "@/components/ui/popover";

/**
 * UptimeSparkline — click-to-open popover showing the last hour of health
 * pings as a sparkline. Feed `samples` as a list of `{ts, latency, status}`
 * where `latency` is ms and `status` is "up"|"slow"|"down".
 */
export default function UptimeSparkline({ samples, currentStatus, currentLatency }) {
  const [open, setOpen] = useState(false);

  const cfg = {
    up:      { c: "#10B981", label: "Backend healthy" },
    slow:    { c: "#F59E0B", label: "Backend slow" },
    down:    { c: "#EF4444", label: "Backend unreachable" },
    unknown: { c: "#64748B", label: "Backend status unknown" },
  }[currentStatus] || { c: "#64748B", label: "?" };

  const stats = useMemo(() => {
    const okSamples = samples.filter(s => s.status !== "down" && s.latency != null);
    if (okSamples.length === 0) return { min: null, max: null, avg: null, uptimePct: 100, total: samples.length };
    const lats = okSamples.map(s => s.latency);
    const min = Math.min(...lats);
    const max = Math.max(...lats);
    const avg = Math.round(lats.reduce((a, b) => a + b, 0) / lats.length);
    const downCount = samples.filter(s => s.status === "down").length;
    const uptimePct = samples.length > 0
      ? Math.round(((samples.length - downCount) / samples.length) * 1000) / 10
      : 100;
    return { min, max, avg, uptimePct, total: samples.length, downCount };
  }, [samples]);

  const sparkline = useMemo(() => {
    if (samples.length < 2) return null;
    const w = 260, h = 48;
    const lats = samples.map(s => s.latency ?? 0);
    const maxY = Math.max(200, ...lats);
    const step = w / Math.max(1, samples.length - 1);
    const pts = samples.map((s, i) => {
      const x = i * step;
      const y = h - ((s.latency ?? 0) / maxY) * (h - 4) - 2;
      return { x, y, s };
    });
    const path = pts.map((p, i) => `${i === 0 ? "M" : "L"}${p.x.toFixed(1)},${p.y.toFixed(1)}`).join(" ");
    return { w, h, path, pts, maxY };
  }, [samples]);

  return (
    <Popover open={open} onOpenChange={setOpen}>
      <PopoverTrigger asChild>
        <button type="button" data-testid="admin-uptime-dot"
          title={`${cfg.label}${currentLatency != null ? ` · ${currentLatency} ms` : ""} · click for last-hour chart`}
          aria-label={cfg.label}
          className="ml-1 inline-flex items-center gap-1.5 focus:outline-none focus:ring-1 focus:ring-[#00F0FF]/40 rounded-full">
          <span className="relative inline-flex w-2 h-2">
            {currentStatus === "up" && (
              <span className="absolute inline-flex w-full h-full rounded-full opacity-60 animate-ping"
                    style={{ background: cfg.c }}/>
            )}
            <span className="relative inline-flex w-2 h-2 rounded-full" style={{ background: cfg.c }}/>
          </span>
        </button>
      </PopoverTrigger>
      <PopoverContent
        data-testid="admin-uptime-popover"
        align="start"
        sideOffset={12}
        className="w-80 bg-[#121620] border-[#1E2638] text-white p-4 space-y-3">
        <div className="flex items-center justify-between">
          <div className="font-mono text-[10px] uppercase tracking-widest text-[#94A3B8]">// Backend heartbeat</div>
          <div className="font-mono text-[10px]" style={{ color: cfg.c }}>{cfg.label}</div>
        </div>

        {sparkline ? (
          <svg width="100%" viewBox={`0 0 ${sparkline.w} ${sparkline.h}`} className="rounded bg-[#0B0E14]" data-testid="admin-uptime-sparkline">
            <line x1="0" y1={sparkline.h / 2} x2={sparkline.w} y2={sparkline.h / 2}
                  stroke="#1E2638" strokeWidth="0.5" strokeDasharray="2 3"/>
            <path d={sparkline.path} fill="none" stroke={cfg.c} strokeWidth="1.5"/>
            {sparkline.pts.map((p, i) => (
              <circle key={i} cx={p.x} cy={p.y} r={p.s.status === "down" ? 2 : 1}
                      fill={p.s.status === "down" ? "#EF4444" : cfg.c}/>
            ))}
          </svg>
        ) : (
          <div className="h-12 flex items-center justify-center text-[10px] font-mono text-[#94A3B8] bg-[#0B0E14] rounded">
            Collecting samples… come back in a minute.
          </div>
        )}

        <div className="grid grid-cols-4 gap-2 text-[10px] font-mono">
          <Stat label="Uptime" value={`${stats.uptimePct}%`}/>
          <Stat label="Min" value={stats.min != null ? `${stats.min}ms` : "—"}/>
          <Stat label="Avg" value={stats.avg != null ? `${stats.avg}ms` : "—"}/>
          <Stat label="Max" value={stats.max != null ? `${stats.max}ms` : "—"}/>
        </div>
        <div className="text-[10px] font-mono text-[#94A3B8] flex items-baseline justify-between">
          <span>{stats.total} sample{stats.total === 1 ? "" : "s"}</span>
          <span>Ping every 30 s</span>
        </div>
      </PopoverContent>
    </Popover>
  );
}

function Stat({ label, value }) {
  return (
    <div className="rounded bg-[#0B0E14] border border-[#1E2638] px-2 py-1">
      <div className="text-[9px] text-[#94A3B8] uppercase tracking-widest">{label}</div>
      <div className="text-[11px] text-white font-black">{value}</div>
    </div>
  );
}
